import { updateAppCodes } from '@redux/sensors/sensors.reducer';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@services/baseQueryWithReAuth';
import { getFileNameFromHttpResponse } from '@utils/file.utils';

import {
  AutomatonCommand,
  C2StorageConfiguration,
  CameraCommand,
  EquipmentCommand,
  FlirCommand,
  GlobalConfiguration,
  GroundStationCommand,
  HolodroneCommand,
  Mc2Command,
  SkyJackerCommand,
  XentaCommand,
} from '@/types/c2/c2.types';
import { Coordinates, ImportItem } from '@/types/commons/commons.types';
import { FileDirectoryDownloadResponseData } from '@/types/download.types';
import { ActiveSensorCriteria } from '@/types/replay/replay.types';
import { SensorConfiguration } from '@/types/sensor/configuration.types';
import { SensorConfigurationCriteria } from '@/types/sensor/sensor.types';
import { CameraLiteConfiguration, SubSensorTypeEnum } from '@/types/sensor/sensors';
import { PerimeterCamera, PerimeterCameraConfigurationCriteria } from '@/types/sensor/sensors/perimeterCamera.types';

export const C2_URL = import.meta.env.VITE_C2_URL;

export const c2Api = createApi({
  reducerPath: 'c2Api',
  baseQuery: baseQueryWithReAuth,
  keepUnusedDataFor: 0,
  tagTypes: ['AppCodes', 'StorageConfiguration'],
  endpoints: (builder) => ({
    getAppCodes: builder.query<string[], string>({
      query: (site) => ({
        url: `${C2_URL}/${site}/sensors/appCodes`,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(updateAppCodes(data));
      },
      providesTags: [{ type: 'AppCodes', id: 'APPCODE_LIST' }],
    }),
    getStorageConfiguration: builder.query<C2StorageConfiguration, string>({
      query: (site) => ({
        url: `${C2_URL}/storage-configurations/default`,
        params: {
          site: site,
        },
      }),
      providesTags: [{ type: 'StorageConfiguration', id: 'STORAGE_CONFIGURATION' }],
    }),
    getConfigVersion: builder.query<Map<string, string>, void>({
      query: () => ({
        url: 'export/version',
      }),
    }),
    getSensorLogs: builder.mutation<FileDirectoryDownloadResponseData, { site: string; id: string }>({
      query: ({ site, id }) => ({
        url: `${C2_URL}/sensor/${site}/logs`,
        method: 'GET',
        params: { id },
        responseHandler: async (response) => ({
          blob: await response.blob(),
          name: getFileNameFromHttpResponse(response, 'export-dfb6-logs.zip'),
        }),
      }),
    }),
    exportSensorConfigurations: builder.mutation<FileDirectoryDownloadResponseData, SensorConfigurationCriteria>({
      query: ({ site, ...rest }) => ({
        url: `${C2_URL}/sensors/export`,
        method: 'GET',
        params: {
          sites: [site],
          ...rest,
        },
        responseHandler: async (response) => ({
          blob: await response.blob(),
          name: getFileNameFromHttpResponse(response, 'export-sensors.json'),
        }),
      }),
    }),
    exportPerimeterCameras: builder.mutation<FileDirectoryDownloadResponseData, PerimeterCameraConfigurationCriteria>({
      query: ({ site, ...rest }) => ({
        url: `${C2_URL}/perimeter-cameras/export`,
        method: 'GET',
        params: {
          sites: [site],
          ...rest,
        },
        responseHandler: async (response) => ({
          blob: await response.blob(),
          name: getFileNameFromHttpResponse(response, 'export-perimeter-cameras.json'),
        }),
      }),
    }),
    retrieveCoordinatesAltitude: builder.query<number[], { coordinates: Coordinates[]; instanceId?: string }>({
      query: ({ coordinates }) => ({
        url: `${C2_URL}/dtm`,
        method: 'POST',
        body: coordinates,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { coordinates, instanceId } = queryArgs;
        return `${endpointName}-${instanceId ?? 'default'}-${JSON.stringify(coordinates)}`;
      },
    }),
    resetSituation: builder.mutation<void, string[]>({
      query: (sites) => ({
        url: `${C2_URL}/situations/reset`,
        method: 'POST',
        params: {
          sites: sites,
        },
      }),
    }),
    createSensorConfiguration: builder.mutation<
      SensorConfiguration,
      { site: string; sensorConfiguration: Partial<SensorConfiguration> }
    >({
      query: ({ site, sensorConfiguration }) => ({
        url: `${C2_URL}/${site}/sensors`,
        method: 'POST',
        body: sensorConfiguration,
      }),
    }),
    createConfiguration: builder.mutation<void, { site: string; configuration: GlobalConfiguration }>({
      query: ({ site, configuration }) => ({
        url: `${C2_URL}/import/configuration`,
        method: 'POST',
        params: {
          site: site,
        },
        body: configuration,
      }),
    }),
    previewImportConfiguration: builder.mutation<ImportItem[], { site: string; configuration: GlobalConfiguration }>({
      query: ({ site, configuration }) => ({
        url: `${C2_URL}/import/configuration/preview`,
        method: 'POST',
        params: {
          site: site,
        },
        body: configuration,
      }),
    }),
    exportConfiguration: builder.mutation<void, { site: string; versions: Map<string, string> }>({
      query: ({ site, versions }) => ({
        url: `${C2_URL}/export/configuration`,
        method: 'POST',
        params: {
          site: site,
        },
        body: Object.fromEntries(versions),
      }),
    }),
    postAutomatonCommand: builder.mutation<void, { site: string; appCode: string; command: AutomatonCommand }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/commands/automaton`,
        method: 'POST',
        body: command,
      }),
    }),
    postCameraCommand: builder.mutation<void, { site: string; appCode: string; command: CameraCommand }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/commands/camera`,
        method: 'POST',
        body: command,
      }),
    }),
    postXentaCommand: builder.mutation<void, { site: string; appCode: string; command: XentaCommand }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/commands/xenta`,
        method: 'POST',
        body: command,
      }),
    }),
    postGroundStationCommand: builder.mutation<void, { site: string; appCode: string; command: GroundStationCommand }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/commands/ground-station`,
        method: 'POST',
        body: command,
      }),
    }),
    postHolodroneCommand: builder.mutation<void, { site: string; appCode: string; command: HolodroneCommand }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/commands/holodrone`,
        method: 'POST',
        body: command,
      }),
    }),
    postSkyJackerCommand: builder.mutation<void, { site: string; appCode: string; command: SkyJackerCommand }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/commands/skyjacker`,
        method: 'POST',
        body: command,
      }),
    }),
    postMajesCommand: builder.mutation<void, { site: string; appCode: string; command: Mc2Command }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/commands/mc2`,
        method: 'POST',
        body: command,
      }),
    }),
    postFlirCommand: builder.mutation<void, { site: string; appCode: string; command: FlirCommand }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/commands/flir`,
        method: 'POST',
        body: command,
      }),
    }),
    postLaunchBatchMigrateBlobs: builder.mutation<void, void>({
      query: () => ({
        url: `${C2_URL}/batchs/migrateBlobs`,
        method: 'POST',
      }),
    }),
    updateSensorConfiguration: builder.mutation<
      void,
      { id: number; site: string; sensor: Partial<SensorConfiguration> }
    >({
      query: ({ id, site, sensor }) => ({
        url: `${C2_URL}/${site}/sensors/${id}`,
        method: 'PUT',
        body: sensor,
      }),
    }),
    updateSensorActivation: builder.mutation<
      void,
      { id: number; site: string; appCode: string; active: boolean; type?: SubSensorTypeEnum; codes?: string[] }
    >({
      query: ({ id, site, appCode, active, type, codes }) => ({
        url: `${C2_URL}/${site}/${appCode}/sensors/${id}/active`,
        method: 'PATCH',
        body: JSON.stringify(active),
        headers: { 'Content-Type': 'application/json' },
        params: {
          type,
          codes,
        },
      }),
    }),
    updateSensorMaintenance: builder.mutation<
      void,
      { id: number; site: string; appCode: string; maintenance: boolean; type?: SubSensorTypeEnum; codes?: string[] }
    >({
      query: ({ id, site, appCode, maintenance, type, codes }) => ({
        url: `${C2_URL}/${site}/${appCode}/sensors/${id}/maintenance`,
        method: 'PATCH',
        body: JSON.stringify(maintenance),
        headers: { 'Content-Type': 'application/json' },
        params: {
          type,
          codes,
        },
      }),
    }),
    updateStorageConfiguration: builder.mutation<void, { site: string; storageConfiguration: C2StorageConfiguration }>({
      query: ({ site, storageConfiguration }) => ({
        url: `${C2_URL}/storage-configurations/${site}`,
        method: 'PUT',
        body: storageConfiguration,
      }),
      invalidatesTags: [{ type: 'StorageConfiguration', id: 'STORAGE_CONFIGURATION' }],
    }),
    deleteSensorConfiguration: builder.mutation<void, { site: string; appCode: string; id: number }>({
      query: ({ site, appCode, id }) => ({
        url: `${C2_URL}/${site}/${appCode}/sensors/${id}`,
        method: 'DELETE',
      }),
    }),
    getAvailableRawRanges: builder.query<{ from: string; to: string }[], { site: string; from: string; to: string }>({
      query: ({ site, from, to }) => ({
        url: `${C2_URL}/sensor/${site}/replay/available-raw-ranges`,
        method: 'GET',
        params: {
          site,
          from,
          to,
        },
      }),
    }),
    getSensorsWithAvailableRawsForRange: builder.query<
      { sensorCodes: string[] },
      { site: string; from: string; to: string }
    >({
      query: ({ site, from, to }) => ({
        url: `${C2_URL}/sensor/${site}/replay/codes`,
        method: 'GET',
        params: {
          site,
          from,
          to,
        },
      }),
    }),
    importSensorConfigurations: builder.mutation<void, { site: string; fileContent: SensorConfiguration[] }>({
      query: ({ site, fileContent }) => {
        return {
          url: `${C2_URL}/import/sensor-configurations`,
          method: 'POST',
          params: {
            site,
          },
          body: fileContent,
        };
      },
    }),
    importPerimetersCamera: builder.mutation<void, { site: string; fileContent: PerimeterCamera[] }>({
      query: ({ site, fileContent }) => {
        return {
          url: `${C2_URL}/import/perimeter-cameras`,
          method: 'POST',
          params: {
            site: site,
          },
          body: fileContent,
        };
      },
    }),
    updatePerimeterCameraConfiguration: builder.mutation<void, { id: number; site: string; pericam: PerimeterCamera }>({
      query: ({ id, site, pericam }) => ({
        url: `${C2_URL}/${site}/perimeter-cameras/${id}`,
        method: 'PUT',
        body: pericam,
      }),
    }),
    postEquipmentCommand: builder.mutation<void, { site: string; appCode: string; command: EquipmentCommand }>({
      query: ({ site, appCode, command }) => ({
        url: `${C2_URL}/${site}/${appCode}/equipments/commands`,
        method: 'POST',
        body: command,
      }),
    }),
    initModel: builder.mutation<void, void>({
      query: () => ({
        url: `${C2_URL}/models/init`,
        method: 'POST',
      }),
    }),
    updateEffectiveTimeOff: builder.mutation<
      void,
      { site: string; appCode: string; id: number; subCameraId: number; displayExtendTimePopup: boolean }
    >({
      query: ({ id, site, appCode, subCameraId, displayExtendTimePopup }) => ({
        url: `${C2_URL}/${site}/${appCode}/sensors/videoOffTime/${id}/${subCameraId}`,
        method: 'PATCH',
        params: {
          displayExtendTimePopup: displayExtendTimePopup,
        },
      }),
    }),
    getAvailableCameras: builder.query<CameraLiteConfiguration[], ActiveSensorCriteria>({
      query: ({ site, from, to }) => ({
        url: `${C2_URL}/config/${site}/dvr/available-cameras`,
        params: {
          from: from,
          to: to,
        },
      }),
    }),
  }),
});

export const {
  useGetAppCodesQuery,
  usePostAutomatonCommandMutation,
  usePostCameraCommandMutation,
  usePostXentaCommandMutation,
  usePostSkyJackerCommandMutation,
  usePostFlirCommandMutation,
  useGetSensorLogsMutation,
  useExportSensorConfigurationsMutation,
  useExportPerimeterCamerasMutation,
  useRetrieveCoordinatesAltitudeQuery,
  useLazyRetrieveCoordinatesAltitudeQuery,
  useGetAvailableRawRangesQuery,
  useGetSensorsWithAvailableRawsForRangeQuery,
  useGetStorageConfigurationQuery,
  useUpdateSensorActivationMutation,
  useUpdateStorageConfigurationMutation,
  useImportSensorConfigurationsMutation,
  useImportPerimetersCameraMutation,
  useUpdateSensorMaintenanceMutation,
  useCreateSensorConfigurationMutation,
  useDeleteSensorConfigurationMutation,
  useUpdateSensorConfigurationMutation,
  useResetSituationMutation,
  useUpdatePerimeterCameraConfigurationMutation,
  usePostHolodroneCommandMutation,
  usePostEquipmentCommandMutation,
  usePostGroundStationCommandMutation,
  usePostMajesCommandMutation,
  useInitModelMutation,
  useUpdateEffectiveTimeOffMutation,
  useGetAvailableCamerasQuery,
} = c2Api;
